import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, X, BookOpen, Trophy } from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import ChaptersOnboarding from '../components/chapters/ChaptersOnboarding';
import DailyQuestionModal from '../components/chapters/DailyQuestionModal';
import { useChaptersData } from '../hooks/useChaptersData';
import { useDailyQuestion } from '../hooks/useDailyQuestion';
import { axiosInstance } from '../services/api';
import { createChildProfile } from '../services/childChaptersAPI';
import { getUser } from '../utils/secureStorage';
import '../styles/hub-chapters.css';

// ── Child age-band chapter definitions ──
function getAgeBand(age) {
  if (age == null) return 'adult';
  if (age < 4) return 'infant';
  if (age < 9) return 'young_child';
  if (age < 13) return 'tween';
  if (age < 18) return 'teen';
  return 'adult';
}

const CHILD_CHAPTERS = {
  infant: [
    { index: 1, title: 'Welcome to the World', emoji: '🌅', description: 'Birth story — the day, the room, the first cry, the first hold.', note: 'Only you can tell this story.' },
    { index: 2, title: 'First Days at Home', emoji: '🏠', description: 'Coming home, the first nights, the chaos and wonder.', note: 'Guardian-authored.' },
    { index: 3, title: 'Who They Already Are', emoji: '✨', description: 'Personality already peeking through — sounds, sleep, what calms them.', note: 'Your early observations.' },
    { index: 4, title: 'Our Family', emoji: '🤝', description: 'Grandparents, siblings, cousins — who has already held them.', note: 'Guardian-authored.' },
    { index: 5, title: 'Letters to the Future', emoji: '✉️', description: 'Messages from today for Baby to read someday.', note: 'Guardian writes to Baby.' },
  ],
  young_child: [
    { index: 1, title: 'My Beginning', emoji: '🌱', description: 'Name story, birth story, early memories.', note: 'Guardian-authored.' },
    { index: 2, title: 'Play & Learning', emoji: '🎨', description: 'School, discovery, how they learn through play.', note: 'Guardian-authored.' },
    { index: 3, title: 'What I Love', emoji: '⭐', description: 'Favorites right now — food, toys, shows, songs, games.', note: 'Guardian + child input.' },
    { index: 4, title: 'Friends & Family', emoji: '🤝', description: 'Best friends, cousins, grandparents, bonds they\'re building.', note: 'Guardian-authored.' },
    { index: 5, title: 'Growing Up', emoji: '🚀', description: 'Milestones, firsts, what they\'re becoming, what they dream about.', note: 'Guardian-authored.' },
  ],
  tween: [
    { index: 1, title: 'Who I Am', emoji: '🪞', description: 'Identity forming — values, personality, how they see themselves.', note: 'Child leads.' },
    { index: 2, title: 'School Life', emoji: '📚', description: 'Subjects, teachers, challenges, achievements.', note: 'Mixed authorship.' },
    { index: 3, title: 'Passions & Talents', emoji: '🎯', description: 'What they\'re good at, what they love doing.', note: 'Child leads.' },
    { index: 4, title: 'My World', emoji: '🌍', description: 'Friends, community, social life, who matters to them.', note: 'Mixed authorship.' },
    { index: 5, title: 'Looking Ahead', emoji: '🔭', description: 'Dreams, plans, questions about the future.', note: 'Child leads.' },
  ],
  teen: [
    { index: 1, title: 'My Identity', emoji: '🧭', description: 'Who I\'m becoming — values, beliefs, how I see the world.', note: 'Child leads.' },
    { index: 2, title: 'High School', emoji: '🏫', description: 'Experiences, memories, friendships, defining moments.', note: 'Child leads.' },
    { index: 3, title: 'Passions & Skills', emoji: '💡', description: 'What I\'m developing, what I\'m proud of.', note: 'Child leads.' },
    { index: 4, title: 'My People', emoji: '❤️', description: 'Friendships, bonds, relationships that matter.', note: 'Child leads.' },
    { index: 5, title: 'Future Dreams', emoji: '🚀', description: 'Plans, aspirations, fears, hopes for what comes next.', note: 'Child leads.' },
  ],
};

// Accent color constants for children
const CHILD_COLORS = [
  { key: 'orange', bg: '#fff3ed', border: '#E65100', text: '#E65100', avatar: '#E65100', light: '#f5c4a0' },
  { key: 'blue', bg: '#e6f1fb', border: '#185FA5', text: '#185FA5', avatar: '#185FA5', light: '#b5d4f4' },
];
const SELF_COLOR = { key: 'olive', bg: '#f0f4e8', border: '#556B2F', text: '#556B2F', avatar: '#556B2F', light: '#d4e0b8' };

/**
 * MyLifeChapters Page - Chapters at Scale Design
 *
 * Accordion-based chapters with year groupings, search/filter,
 * and item cards with last comment preview.
 * Supports profile switcher for viewing children's chapters.
 */
function MyLifeChapters() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Person switcher state
  const [selectedPersonId, setSelectedPersonId] = useState(searchParams.get('person') || null);
  const [children, setChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(true);

  const {
    profile,
    chapters,
    legacyStats,
    stories: personStories,
    hasCompletedOnboarding,
    loading,
    error,
    refetch,
  } = useChaptersData(selectedPersonId);

  const {
    questionData,
    showModal: showDailyQuestion,
    handleAnswer: handleDailyAnswer,
    handleSkip: handleDailySkip,
    dismissModal: dismissDailyQuestion,
  } = useDailyQuestion();

  const [openChapters, setOpenChapters] = useState(new Set());
  const [collapsedYears, setCollapsedYears] = useState({});
  const [chapterFilters, setChapterFilters] = useState({});
  const [chapterSearches, setChapterSearches] = useState({});
  const [sortOrders, setSortOrders] = useState({});
  const [fabOpen, setFabOpen] = useState(false);
  const [highlightedChapterId, setHighlightedChapterId] = useState(null);
  const chapterRefs = useRef({});

  // Fetch children from grouped family API
  useEffect(() => {
    const fetchChildren = async () => {
      try {
        setChildrenLoading(true);
        const response = await axiosInstance.get('/genealogy/people/my_family_grouped/');
        const data = response.data;
        const minorChildren = (data.children || []).filter(c => c.is_minor);
        setChildren(minorChildren);
      } catch (err) {
        console.error('Error fetching children for switcher:', err);
        setChildren([]);
      } finally {
        setChildrenLoading(false);
      }
    };
    fetchChildren();
  }, []);

  // Get accent color for currently selected person
  const getChildIndex = (childId) => children.findIndex(c => c.id === childId);
  const getChildColor = (childId) => {
    const idx = getChildIndex(childId);
    return idx >= 0 ? CHILD_COLORS[idx % CHILD_COLORS.length] : SELF_COLOR;
  };
  const activeColor = selectedPersonId ? getChildColor(selectedPersonId) : SELF_COLOR;
  const isChildView = !!selectedPersonId;

  // Auto-generate child chapters when a child is selected and has no chapters
  useEffect(() => {
    if (!selectedPersonId || !isChildView || loading) return;
    if (chapters && chapters.length > 0) return;

    const autoGenerate = async () => {
      try {
        await createChildProfile(selectedPersonId, {});
        refetch();
      } catch (err) {
        console.log('Auto-generate child chapters:', err.message || 'skipped');
      }
    };
    autoGenerate();
  }, [selectedPersonId, isChildView, loading, chapters?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wrapped answer handler: after QotD answer, refetch data and highlight the chapter
  const handleDailyAnswerWithHighlight = useCallback(async (data, mediaFiles) => {
    const result = await handleDailyAnswer(data, mediaFiles);
    if (result?.chapter_id) {
      await refetch();
      const targetId = result.chapter_id;
      setOpenChapters(prev => new Set([...prev, targetId]));
      setHighlightedChapterId(targetId);
      setTimeout(() => {
        chapterRefs.current[targetId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      setTimeout(() => setHighlightedChapterId(null), 3000);
    }
    return result;
  }, [handleDailyAnswer, refetch]);

  // Toggle chapter open/close
  const toggleChapter = (chapterId) => {
    setOpenChapters((prev) => {
      const next = new Set(prev);
      if (next.has(chapterId)) {
        next.delete(chapterId);
      } else {
        next.add(chapterId);
      }
      return next;
    });
  };

  const toggleYearCollapse = (chapterId, yearKey) => {
    setCollapsedYears((prev) => ({
      ...prev,
      [`${chapterId}-${yearKey}`]: !prev[`${chapterId}-${yearKey}`],
    }));
  };

  const setChapterFilter = (chapterId, filter) => {
    setChapterFilters((prev) => ({ ...prev, [chapterId]: filter }));
  };

  const setChapterSearch = (chapterId, search) => {
    setChapterSearches((prev) => ({ ...prev, [chapterId]: search }));
  };

  const setSortOrder = (chapterId, order) => {
    setSortOrders((prev) => ({ ...prev, [chapterId]: order }));
  };

  // Chapter colors - use child accent color or default
  const defaultChapterColors = ['#6B8E23', '#8B7355', '#556B2F', '#7B6B8E', '#8E6B23'];
  const chapterColors = isChildView
    ? [activeColor.border, activeColor.border, activeColor.border, activeColor.border, activeColor.border]
    : defaultChapterColors;

  // Loading state
  if (loading && !chapters.length) {
    return (
      <HubLayout activeView="life">
        <div className="hub-container">
          <div className="hub-loading">
            <div className="hub-loading-spinner"></div>
            <p style={{ fontFamily: "'Lora', Georgia, serif", fontSize: '16px' }}>Loading your story...</p>
          </div>
        </div>
      </HubLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <HubLayout activeView="life">
        <div className="hub-container">
          <div style={{ maxWidth: '600px', margin: '60px auto', padding: '40px', textAlign: 'center', background: 'white', borderRadius: '12px', border: '1px solid #E8E4D8' }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>😔</span>
            <h3 style={{ fontFamily: "'Lora', Georgia, serif", fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
              Something went wrong
            </h3>
            <p style={{ color: '#8A8A7A', marginBottom: '20px' }}>
              We couldn't load your story. Please try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#556B2F',
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      </HubLayout>
    );
  }

  // Onboarding if no chapters yet (only for self view)
  if (!isChildView && !hasCompletedOnboarding) {
    return (
      <ChaptersOnboarding userName={profile?.first_name || 'Friend'} birthYear={profile?.birth_year || null} />
    );
  }

  // Calculate totals
  const totalChapters = chapters.length;
  const totalMemories = chapters.reduce((sum, ch) => sum + (ch.memory_count || 0), 0);
  const totalMilestones = chapters.reduce((sum, ch) => sum + (ch.milestone_count || 0), 0);
  const totalCapsules = legacyStats?.capsule_count || 0;
  const totalPhotos = legacyStats?.photo_count || 0;
  const totalStories = personStories?.length || 0;

  // Format birth info
  const getBirthInfo = () => {
    if (!profile?.birth_year) return '';
    const month = profile.birth_month
      ? new Date(2000, profile.birth_month - 1).toLocaleString('default', { month: 'long' })
      : '';
    const day = profile.birth_day ? profile.birth_day : '';
    if (month && day) return `Born ${month} ${day}, ${profile.birth_year}`;
    if (month) return `Born ${month} ${profile.birth_year}`;
    return `Born ${profile.birth_year}`;
  };

  const getTagClass = (type) => {
    if (type === 'celebration' || type === 'funny_moment') return 'cel';
    if (type === 'personal_experience' || type === 'life_lesson') return 'per';
    return 'fam';
  };

  const getStoryEmoji = (type) => {
    const emojiMap = {
      family_memory: '👨‍👩‍👧',
      personal_experience: '🏛️',
      celebration: '🎉',
      funny_moment: '😄',
      life_lesson: '💡',
      historical_event: '📚',
      tragic_event: '💔',
    };
    return emojiMap[type] || '📝';
  };

  const getAvatarClass = (name) => {
    if (!name) return 'a1';
    const sum = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `a${(sum % 4) + 1}`;
  };

  const getChapterItems = (chapter) => {
    const milestones = (chapter.milestones_preview || []).map((m) => ({
      ...m,
      itemType: 'milestone',
      icon: m.icon || '🏆',
      tagClass: 'ms-tag',
      tagLabel: 'Milestone',
    }));

    const memories = (chapter.memories_preview || []).map((m) => ({
      ...m,
      itemType: 'memory',
      icon: getStoryEmoji(m.story_type),
      tagClass: getTagClass(m.story_type),
      tagLabel: m.story_type_label || 'Memory',
    }));

    const capsules = (chapter.capsules_preview || []).map((c) => ({
      ...c,
      itemType: 'capsule',
      icon: '💌',
      tagClass: 'cap-tag',
      tagLabel: 'Capsule',
    }));

    let items = [...milestones, ...memories, ...capsules];

    const filter = chapterFilters[chapter.id] || 'all';
    if (filter === 'memories') items = items.filter((i) => i.itemType === 'memory');
    else if (filter === 'milestones') items = items.filter((i) => i.itemType === 'milestone');
    else if (filter === 'capsules') items = items.filter((i) => i.itemType === 'capsule');

    const search = (chapterSearches[chapter.id] || '').toLowerCase();
    if (search) items = items.filter((i) => i.title?.toLowerCase().includes(search));

    const sortOrder = sortOrders[chapter.id] || 'newest';
    items.sort((a, b) => {
      const yearA = a.year || 0;
      const yearB = b.year || 0;
      return sortOrder === 'newest' ? yearB - yearA : yearA - yearB;
    });

    return items;
  };

  const groupItemsByYear = (items) => {
    const groups = {};
    items.forEach((item) => {
      const year = item.year || 'Unknown';
      if (!groups[year]) groups[year] = [];
      groups[year].push(item);
    });
    return groups;
  };

  const needsToolbar = (chapter) => {
    return (chapter.memory_count || 0) + (chapter.milestone_count || 0) > 6;
  };

  const handleItemClick = (item, e) => {
    e.stopPropagation();
    if (item.itemType === 'milestone') navigate(`/my-life/milestones/${item.id}`);
    else if (item.itemType === 'memory') {
      if (item.life_event_id) navigate(`/stories/${item.life_event_id}`);
      else navigate(`/my-life/memories/${item.id}`);
    } else if (item.itemType === 'capsule') navigate(`/timecapsule/${item.id}`);
  };

  // Get the current user's info for the switcher
  const userData = getUser() || {};
  const userName = userData.first_name || profile?.first_name || 'You';
  const selectedChild = children.find(c => c.id === selectedPersonId);

  // Determine child age band and get child-specific chapter definitions
  const childAgeBand = isChildView ? getAgeBand(selectedChild?.age) : 'adult';
  const childChapterDefs = childAgeBand !== 'adult' ? CHILD_CHAPTERS[childAgeBand] : null;

  // Overlay child chapter titles/descriptions onto the backend chapter data
  const getChapterDisplay = (chapter, index) => {
    if (childChapterDefs && childChapterDefs[index]) {
      const def = childChapterDefs[index];
      return { title: def.title, emoji: def.emoji, description: def.description, note: def.note };
    }
    return { title: chapter.title, emoji: null, description: null, note: null };
  };

  return (
    <HubLayout activeView="life">
      {!isChildView && showDailyQuestion && hasCompletedOnboarding && questionData?.question && (
        <DailyQuestionModal
          questionData={questionData}
          onAnswer={handleDailyAnswerWithHighlight}
          onSkip={handleDailySkip}
          onDismiss={dismissDailyQuestion}
        />
      )}
      <div className="chapters-scale-container">

        {/* Profile Switcher - only show if user has minor children */}
        {children.length > 0 && (
          <div style={{
            display: 'flex', gap: '12px', padding: '16px 0 20px',
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          }}>
            {/* Self card */}
            <button
              onClick={() => setSelectedPersonId(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 18px', borderRadius: '14px', border: `2.5px solid ${!selectedPersonId ? SELF_COLOR.border : '#E8E4DA'}`,
                background: !selectedPersonId ? SELF_COLOR.bg : '#fff',
                cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: SELF_COLOR.avatar, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '15px', fontWeight: 700,
              }}>
                {userName.charAt(0).toUpperCase()}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#2C3E1A', fontFamily: "'Source Sans 3', sans-serif" }}>{userName}</div>
                <div style={{ fontSize: '12px', color: '#999', fontFamily: "'Source Sans 3', sans-serif" }}>My Story</div>
              </div>
            </button>

            {/* Child cards */}
            {children.map((child, idx) => {
              const color = CHILD_COLORS[idx % CHILD_COLORS.length];
              const isActive = selectedPersonId === child.id;
              return (
                <button
                  key={child.id}
                  onClick={() => setSelectedPersonId(child.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 18px', borderRadius: '14px', border: `2.5px solid ${isActive ? color.border : '#E8E4DA'}`,
                    background: isActive ? color.bg : '#fff',
                    cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: color.avatar, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '15px', fontWeight: 700,
                  }}>
                    {child.first_name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#2C3E1A', fontFamily: "'Source Sans 3', sans-serif" }}>{child.first_name}</div>
                    <div style={{ fontSize: '12px', color: '#999', fontFamily: "'Source Sans 3', sans-serif" }}>
                      {child.age != null ? `Age ${child.age}` : 'Child'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Page Header */}
        <div className="scale-page-header" style={isChildView ? { borderLeft: `4px solid ${activeColor.border}` } : {}}>
          <div className="scale-page-header-icon" style={isChildView ? { background: activeColor.bg } : {}}>
            {isChildView ? '📒' : '📕'}
          </div>
          <div>
            <h1 style={isChildView ? { color: activeColor.text } : {}}>
              {isChildView
                ? `The Story of ${selectedChild?.first_name || profile?.first_name || 'Child'}`
                : `The Life of ${profile?.name || profile?.first_name || 'You'}`
              }
            </h1>
            <div className="scale-page-header-meta">
              {isChildView && profile?.is_guardian_view
                ? `${getBirthInfo()}${getBirthInfo() ? ' · ' : ''}Recorded by ${profile.guardian_name} (guardian)`
                : getBirthInfo()
              }
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="scale-stats-bar">
          <div className="scale-stat-item">
            <div className="scale-stat-num" style={isChildView ? { color: activeColor.text } : {}}>{totalChapters}</div>
            <div className="scale-stat-label">Chapters</div>
          </div>
          <div className="scale-stat-item">
            <div className="scale-stat-num" style={isChildView ? { color: activeColor.text } : {}}>{totalStories}</div>
            <div className="scale-stat-label">Stories</div>
          </div>
          <div className="scale-stat-item">
            <div className="scale-stat-num" style={isChildView ? { color: activeColor.text } : {}}>{totalMemories}</div>
            <div className="scale-stat-label">Memories</div>
          </div>
          <div className="scale-stat-item">
            <div className="scale-stat-num" style={isChildView ? { color: activeColor.text } : {}}>{totalMilestones}</div>
            <div className="scale-stat-label">Milestones</div>
          </div>
          <div className="scale-stat-item">
            <div className="scale-stat-num" style={isChildView ? { color: activeColor.text } : {}}>{totalPhotos}</div>
            <div className="scale-stat-label">Photos</div>
          </div>
        </div>

        {/* Empty state for child with no chapters AND no stories */}
        {isChildView && chapters.length === 0 && (!personStories || personStories.length === 0) && !loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: activeColor.bg, borderRadius: '16px', margin: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📒</div>
            <h3 style={{ fontFamily: "'Lora', Georgia, serif", fontSize: '20px', fontWeight: 600, marginBottom: '8px', color: activeColor.text }}>
              No chapters yet for {selectedChild?.first_name || 'this child'}
            </h3>
            <p style={{ color: '#8A8A7A', marginBottom: '20px' }}>
              Start recording their story by adding a chapter or milestone.
            </p>
            <button
              onClick={() => navigate(`/tell-a-story?person=${selectedPersonId}`)}
              style={{
                background: activeColor.border, color: 'white', border: 'none',
                padding: '12px 24px', borderRadius: '10px', fontWeight: 700,
                cursor: 'pointer', fontSize: '16px',
              }}
            >
              + Add {selectedChild?.first_name}'s First Story
            </button>
          </div>
        )}

        {/* Stories section - shows LifeEvent stories for this person */}
        {personStories && personStories.length > 0 && (
          <div style={{ margin: '24px 0' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '16px',
            }}>
              <h2 style={{
                fontFamily: "'Lora', Georgia, serif", fontSize: '20px', fontWeight: 700,
                color: isChildView ? activeColor.text : '#2C3E1A', margin: 0,
              }}>
                {isChildView ? `${selectedChild?.first_name || 'Child'}'s Stories` : 'Stories'}
              </h2>
              <button
                onClick={() => navigate(`/tell-a-story${isChildView ? `?person=${selectedPersonId}` : ''}`)}
                style={{
                  background: 'none', border: `1.5px solid ${isChildView ? activeColor.border : '#556B2F'}`,
                  color: isChildView ? activeColor.border : '#556B2F',
                  padding: '6px 14px', borderRadius: '8px', fontSize: '13px',
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                + Add Story
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {personStories.map((story) => (
                <div
                  key={story.id}
                  onClick={() => navigate(`/stories/${story.id}`)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '14px',
                    padding: '16px', background: '#fff', borderRadius: '12px',
                    border: `1px solid ${isChildView ? activeColor.light : '#E8E4D8'}`,
                    cursor: 'pointer', transition: 'box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                    background: isChildView ? activeColor.bg : '#f0f4e8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '20px',
                  }}>
                    📝
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '15px', fontWeight: 600, color: '#2C3E1A',
                      fontFamily: "'Lora', Georgia, serif",
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {story.title}
                    </div>
                    {story.story && (
                      <div style={{
                        fontSize: '13px', color: '#8A8A7A', marginTop: '4px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {story.story.substring(0, 120)}{story.story.length > 120 ? '...' : ''}
                      </div>
                    )}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px',
                      fontSize: '12px', color: '#AAA',
                    }}>
                      {story.date && <span>{story.date}</span>}
                      {story.author && <span>by {story.author}</span>}
                    </div>
                  </div>
                  <span style={{ color: '#ccc', fontSize: '18px', flexShrink: 0, marginTop: '2px' }}>&#8250;</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chapters */}
        {chapters.map((chapter, index) => {
          const isOpen = openChapters.has(chapter.id);
          const chapterColor = chapterColors[index % chapterColors.length];
          const chDisplay = getChapterDisplay(chapter, index);

          const actualMemoryCount = (chapter.memories_preview || []).length;
          const actualMilestoneCount = (chapter.milestones_preview || []).length;
          const actualCapsuleCount = (chapter.capsules_preview || []).length;
          const actualTotalItems = actualMemoryCount + actualMilestoneCount + actualCapsuleCount;

          const memoryCount = chapter.memory_count || 0;
          const milestoneCount = chapter.milestone_count || 0;
          const capsuleCount = chapter.capsule_count || 0;
          const totalItems = memoryCount + milestoneCount + capsuleCount;

          const showToolbar = needsToolbar(chapter);
          const items = getChapterItems(chapter);
          const yearGroups = groupItemsByYear(items);
          const filter = chapterFilters[chapter.id] || 'all';

          const addStoryLabel = isChildView
            ? `+ Add ${selectedChild?.first_name || 'Child'}'s story`
            : 'Add Memory';
          const addBtnBg = isChildView ? activeColor.border : '#556B2F';

          return (
            <div
              key={chapter.id}
              ref={el => { chapterRefs.current[chapter.id] = el; }}
              className={`scale-chapter ${isOpen ? 'open' : ''}${highlightedChapterId === chapter.id ? ' scale-chapter-highlight' : ''}`}
            >
              {/* Chapter Header */}
              <div className="scale-ch-header" onClick={() => toggleChapter(chapter.id)}>
                <div className="scale-ch-num" style={{ background: chapterColor }}>
                  {chapter.number || index + 1}
                </div>
                <div className="scale-ch-info">
                  <h3 className="scale-ch-title">
                    {chDisplay.emoji && <span style={{ marginRight: 6 }}>{chDisplay.emoji}</span>}
                    "{chDisplay.title}"
                  </h3>
                  <div className="scale-ch-subtitle">
                    {chDisplay.description ? (
                      <span className="scale-ch-years" style={{ fontStyle: 'normal' }}>
                        {chDisplay.description}
                      </span>
                    ) : (
                      <span className="scale-ch-years">
                        {chapter.start_year || '?'} – {chapter.end_year || 'Present'}
                      </span>
                    )}
                    {!childChapterDefs && chapter.starter_prompt && totalItems === 0 && (
                      <span className="scale-ch-prompt">✦ {chapter.starter_prompt}</span>
                    )}
                  </div>
                </div>
                <div className="scale-ch-counts">
                  {memoryCount > 0 && (
                    <span className="scale-count-badge mem" style={{
                      background: isChildView ? activeColor.border : '#556B2F',
                      color: '#fff', fontWeight: 700,
                    }}>{memoryCount}</span>
                  )}
                  {milestoneCount > 0 && (
                    <span className="scale-count-badge ms" style={{
                      background: isChildView ? activeColor.border : '#B8860B',
                      color: '#fff', fontWeight: 700,
                    }}>{milestoneCount}</span>
                  )}
                  {capsuleCount > 0 && (
                    <span className="scale-count-badge cap" style={{
                      background: isChildView ? activeColor.border : '#3949AB',
                      color: '#fff', fontWeight: 700,
                    }}>{capsuleCount}</span>
                  )}
                  {totalItems === 0 && (
                    <span className="scale-count-badge mem">0</span>
                  )}
                </div>
                <div className="scale-ch-toggle">▾</div>
              </div>

              {/* Chapter Body */}
              <div className={`scale-ch-body ${!showToolbar ? 'scale-ch-few-items' : ''}`}>
                <div className="scale-ch-body-divider"></div>

                {/* Add Memory / Milestone Buttons */}
                <div
                  className="scale-ch-actions"
                  style={{ display: 'flex', gap: '10px', padding: '8px 0 12px', flexWrap: 'wrap' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => navigate(`/tell-a-story?storyType=family_memory&chapter=${chapter.id}${isChildView ? `&person=${selectedPersonId}` : ''}`)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px', fontSize: '14px', fontWeight: 600,
                      background: addBtnBg, color: 'white', border: 'none',
                      borderRadius: '8px', cursor: 'pointer',
                    }}
                  >
                    <BookOpen size={16} /> {addStoryLabel}
                  </button>
                  <button
                    onClick={() => navigate(`/tell-a-story?storyType=family_memory&chapter=${chapter.id}${isChildView ? `&person=${selectedPersonId}` : ''}`)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px', fontSize: '14px', fontWeight: 600,
                      background: 'white', color: addBtnBg, border: `2px solid ${addBtnBg}`,
                      borderRadius: '8px', cursor: 'pointer',
                    }}
                  >
                    <Trophy size={16} /> Add Milestone
                  </button>
                </div>

                {/* Empty State */}
                {totalItems === 0 && (
                  <div className="scale-ch-empty-msg">
                    {chDisplay.note ? (
                      <span style={{ fontStyle: 'italic', color: '#999' }}>{chDisplay.note}</span>
                    ) : (
                      'No memories yet — answer the prompt above to start filling this chapter!'
                    )}
                  </div>
                )}

                {/* Toolbar - only for chapters with many items */}
                {totalItems > 0 && showToolbar && (
                  <div className="scale-ch-toolbar" onClick={(e) => e.stopPropagation()}>
                    <div className="scale-ch-search">
                      <span className="scale-ch-search-icon">🔍</span>
                      <input
                        type="text"
                        placeholder={`Search ${actualTotalItems} items in this chapter…`}
                        value={chapterSearches[chapter.id] || ''}
                        onChange={(e) => setChapterSearch(chapter.id, e.target.value)}
                      />
                    </div>
                    <div className="scale-ch-filters">
                      <button
                        className={`scale-ch-filter-btn ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setChapterFilter(chapter.id, 'all')}
                      >
                        All ({actualTotalItems})
                      </button>
                      <button
                        className={`scale-ch-filter-btn ${filter === 'memories' ? 'active' : ''}`}
                        onClick={() => setChapterFilter(chapter.id, 'memories')}
                      >
                        Memories ({actualMemoryCount})
                      </button>
                      <button
                        className={`scale-ch-filter-btn ${filter === 'milestones' ? 'active' : ''}`}
                        onClick={() => setChapterFilter(chapter.id, 'milestones')}
                      >
                        Milestones ({actualMilestoneCount})
                      </button>
                      {actualCapsuleCount > 0 && (
                        <button
                          className={`scale-ch-filter-btn ${filter === 'capsules' ? 'active' : ''}`}
                          onClick={() => setChapterFilter(chapter.id, 'capsules')}
                        >
                          Capsules ({actualCapsuleCount})
                        </button>
                      )}
                    </div>
                    <select
                      className="scale-ch-sort"
                      value={sortOrders[chapter.id] || 'newest'}
                      onChange={(e) => setSortOrder(chapter.id, e.target.value)}
                    >
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                    </select>
                  </div>
                )}

                {/* Quick Stats - for larger chapters */}
                {totalItems > 10 && showToolbar && (
                  <div className="scale-ch-quick-stats" onClick={(e) => e.stopPropagation()}>
                    <div className="scale-ch-qs-item">
                      <strong>{memoryCount}</strong> memories
                    </div>
                    <div className="scale-ch-qs-item">
                      <strong>{milestoneCount}</strong> milestones
                    </div>
                    {capsuleCount > 0 && (
                      <div className="scale-ch-qs-item">
                        <strong>{capsuleCount}</strong> capsules
                      </div>
                    )}
                    <div className="scale-ch-qs-item">
                      Spans <strong>{(chapter.end_year || new Date().getFullYear()) - (chapter.start_year || 0)}</strong> years
                    </div>
                  </div>
                )}

                {/* Items - Simple Grid for few items */}
                {totalItems > 0 && !showToolbar && (
                  <div className="scale-items-grid">
                    {items.slice(0, 6).map((item) => (
                      <div
                        key={item.id}
                        className="scale-item"
                        onClick={(e) => handleItemClick(item, e)}
                      >
                        <div className="scale-item-top">
                          <span className="scale-item-icon">{item.icon}</span>
                          <div className="scale-item-content">
                            <div className="scale-item-title">{item.title}</div>
                            <div className="scale-item-foot">
                              <span className={`scale-item-tag ${item.tagClass}`}>
                                {item.tagLabel}
                              </span>
                              <span className="scale-item-date">{item.year || ''}</span>
                            </div>
                          </div>
                          <span className="scale-item-arrow">→</span>
                        </div>
                        {item.last_comment ? (
                          <div className="scale-item-comment">
                            <div className={`scale-item-comment-avatar ${getAvatarClass(item.last_comment.name)}`}>
                              {item.last_comment.name?.charAt(0) || '?'}
                            </div>
                            <div className="scale-item-comment-body">
                              <div className="scale-item-comment-name">{item.last_comment.name}</div>
                              <div className="scale-item-comment-text">{item.last_comment.text}</div>
                            </div>
                            {item.comment_count > 0 && (
                              <div className="scale-item-comment-count">
                                <span className="count-num">{item.comment_count}</span> 💬
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="scale-item-no-comment">
                            💬 No notes yet — be the first
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Items - Year Grouped for many items */}
                {totalItems > 0 && showToolbar && (
                  <>
                    {Object.entries(yearGroups)
                      .sort(([a], [b]) => {
                        const order = sortOrders[chapter.id] || 'newest';
                        return order === 'newest' ? Number(b) - Number(a) : Number(a) - Number(b);
                      })
                      .map(([year, yearItems]) => {
                        const yearKey = `${chapter.id}-${year}`;
                        const isCollapsed = collapsedYears[yearKey];

                        return (
                          <div
                            key={year}
                            className={`scale-year-group ${isCollapsed ? 'collapsed' : ''}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              className="scale-year-label"
                              onClick={() => toggleYearCollapse(chapter.id, year)}
                            >
                              <span className="scale-year-collapse-icon">▾</span>
                              <span className="scale-year-text">{year}</span>
                              <span className="scale-year-count">{yearItems.length} items</span>
                              <span className="scale-year-line"></span>
                            </div>

                            <div className="scale-items-grid">
                              {yearItems.slice(0, 6).map((item) => (
                                <div
                                  key={item.id}
                                  className="scale-item"
                                  onClick={(e) => handleItemClick(item, e)}
                                >
                                  <div className="scale-item-top">
                                    <span className="scale-item-icon">{item.icon}</span>
                                    <div className="scale-item-content">
                                      <div className="scale-item-title">{item.title}</div>
                                      <div className="scale-item-foot">
                                        <span className={`scale-item-tag ${item.tagClass}`}>
                                          {item.tagLabel}
                                        </span>
                                        <span className="scale-item-date">
                                          {item.date_display || item.year || ''}
                                        </span>
                                      </div>
                                    </div>
                                    <span className="scale-item-arrow">→</span>
                                  </div>
                                  {item.last_comment ? (
                                    <div className="scale-item-comment">
                                      <div className={`scale-item-comment-avatar ${getAvatarClass(item.last_comment.name)}`}>
                                        {item.last_comment.name?.charAt(0) || '?'}
                                      </div>
                                      <div className="scale-item-comment-body">
                                        <div className="scale-item-comment-name">{item.last_comment.name}</div>
                                        <div className="scale-item-comment-text">{item.last_comment.text}</div>
                                      </div>
                                      {item.comment_count > 0 && (
                                        <div className="scale-item-comment-count">
                                          <span className="count-num">{item.comment_count}</span> 💬
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="scale-item-no-comment">
                                      💬 No notes yet — be the first
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {yearItems.length > 6 && (
                              <div className="scale-show-more-wrap">
                                <button className="scale-show-more-btn">
                                  Show {yearItems.length - 6} more
                                  <span className="scale-show-more-count">({yearItems.length} total)</span>
                                </button>
                              </div>
                            )}

                            <div className="scale-hidden-summary">
                              <span>
                                📝 <strong>{yearItems.filter((i) => i.itemType === 'memory').length}</strong> memories
                              </span>
                              {yearItems.filter((i) => i.itemType === 'milestone').length > 0 && (
                                <span>
                                  🏆 <strong>{yearItems.filter((i) => i.itemType === 'milestone').length}</strong> milestones
                                </span>
                              )}
                              {yearItems.filter((i) => i.itemType === 'capsule').length > 0 && (
                                <span>
                                  💌 <strong>{yearItems.filter((i) => i.itemType === 'capsule').length}</strong> capsules
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </>
                )}
              </div>
            </div>
          );
        })}

        {/* Add Chapter */}
        {!isChildView && (
          <button
            className="scale-add-chapter"
            onClick={() => navigate('/my-life/add-chapter')}
          >
            + Add a New Chapter
          </button>
        )}
      </div>

      {/* Floating Action Button for Adding Content */}
      <div className={`fab-container ${fabOpen ? 'open' : ''}`}>
        <div className="fab-menu">
          <button
            className="fab-option"
            onClick={() => {
              setFabOpen(false);
              navigate(isChildView ? `/tell-a-story?person=${selectedPersonId}` : '/my-life/add-memory');
            }}
          >
            <span className="fab-option-icon">
              <BookOpen size={20} />
            </span>
            <span className="fab-option-label">
              {isChildView ? `Add ${selectedChild?.first_name || 'Child'}'s Memory` : 'Add a Memory'}
            </span>
          </button>
          <button
            className="fab-option"
            onClick={() => {
              setFabOpen(false);
              navigate(isChildView ? `/tell-a-story?person=${selectedPersonId}` : '/my-life/add-milestone');
            }}
          >
            <span className="fab-option-icon">
              <Trophy size={20} />
            </span>
            <span className="fab-option-label">
              {isChildView ? `Add ${selectedChild?.first_name || 'Child'}'s Milestone` : 'Add a Milestone'}
            </span>
          </button>
        </div>

        <button
          className="fab-main"
          onClick={() => setFabOpen(!fabOpen)}
          aria-label={fabOpen ? 'Close menu' : 'Add content'}
          style={isChildView ? { background: activeColor.border } : {}}
        >
          {fabOpen ? <X size={28} /> : <Plus size={28} />}
        </button>
      </div>

      {fabOpen && (
        <div className="fab-backdrop" onClick={() => setFabOpen(false)} />
      )}
    </HubLayout>
  );
}

export default MyLifeChapters;
